import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useRef,
  useSyncExternalStore,
  type ReactNode,
} from "react"

import { ApplicationContext } from "../application/application-context"
import { DocumentViewStateContext } from "./document-view-state"
import { assertViewStateValue, type ViewStateUpdater, type ViewStateValue } from "./view-state"
import {
  assertUrlViewStateValue,
  decodeUrlViewState,
  encodeUrlViewState,
  type UrlViewStateValue,
} from "./view-state-url"

const ViewStateUrlContext = createContext<ViewStateUrlContextValue | null>(null)
const ViewStateScopeContext = createContext("")

export const ViewStateMode = {
  None: "none",
  URL: "url",
  View: "view",
} as const

export type ViewStateMode = (typeof ViewStateMode)[keyof typeof ViewStateMode]
export type PersistentViewStateMode = Exclude<ViewStateMode, "url">

export const ViewStateScope = {
  Application: "application",
  Route: "route",
} as const

export type ViewStateScope = (typeof ViewStateScope)[keyof typeof ViewStateScope]

export interface ViewStateControlProps {
  viewState?: ViewStateMode
  viewStateId?: string
}

export interface PersistentViewStateControlProps {
  viewState?: PersistentViewStateMode
  viewStateId?: string
}

export interface ViewStateUrlProviderProps {
  children: ReactNode
  values: Readonly<Record<string, string | undefined>>
  onValueChange: (id: string, value: string | undefined) => void
}

interface ViewStateUrlContextValue {
  values: Readonly<Record<string, string | undefined>>
  onValueChange: (id: string, value: string | undefined) => void
}

export interface ViewStateScopeProviderProps {
  children: ReactNode
  id: string
  /** Start an absolute scope instead of nesting under the current route. */
  inherit?: boolean
}

export interface ViewStateFieldOptions<T extends ViewStateValue> extends ViewStateControlProps {
  defaultValue: T
  scope?: ViewStateScope
}

export interface ViewStateField<T extends ViewStateValue> {
  isReady: boolean
  value: T
  setValue: (value: ViewStateUpdater<T>) => void
  reset: () => void
}

type WidenInferredViewState<T> = T extends string
  ? string
  : T extends number
    ? number
    : T extends boolean
      ? boolean
      : T extends null
        ? null
        : T extends undefined
          ? undefined
          : T extends readonly unknown[]
            ? WidenInferredViewState<T[number]>[]
            : T extends object
              ? { [K in keyof T]: WidenInferredViewState<T[K]> }
              : T

type ValidViewState<T> = T extends ViewStateValue ? T : never

export function ViewStateUrlProvider({ children, values, onValueChange }: ViewStateUrlProviderProps) {
  const contextValue = useMemo(() => ({ values, onValueChange }), [onValueChange, values])

  return <ViewStateUrlContext.Provider value={contextValue}>{children}</ViewStateUrlContext.Provider>
}

export function ViewStateScopeProvider({ children, id, inherit = true }: ViewStateScopeProviderProps) {
  const parent = useContext(ViewStateScopeContext)
  const scope = useMemo(() => joinViewStateScope(inherit ? parent : "", id), [id, inherit, parent])

  return <ViewStateScopeContext.Provider value={scope}>{children}</ViewStateScopeContext.Provider>
}

export function useViewStateField<T extends ViewStateValue = never>(
  options: ViewStateFieldOptions<NoInfer<T>>
): ViewStateField<T>
export function useViewStateField<T extends ViewStateValue>(
  options: ViewStateFieldOptions<T>
): ViewStateField<ValidViewState<WidenInferredViewState<T>>>
export function useViewStateField<T extends ViewStateValue>({
  defaultValue,
  viewState = ViewStateMode.None,
  viewStateId,
  scope = ViewStateScope.Route,
}: ViewStateFieldOptions<T>): ViewStateField<T> {
  const application = useContext(ApplicationContext)
  const documentState = useContext(DocumentViewStateContext)
  const urlState = useContext(ViewStateUrlContext)
  const routeScope = useContext(ViewStateScopeContext)
  const id = viewStateId?.trim() ?? ""
  const persistentScope = scope === ViewStateScope.Application ? "" : routeScope
  const persistentKey = joinViewStateScope(persistentScope, id)
  const usesPersistentViewState = viewState === ViewStateMode.View
  const identity = usesPersistentViewState
    ? `${viewState}:${persistentKey}:${scope === ViewStateScope.Route ? (documentState?.instanceId ?? "") : ""}`
    : `${viewState}:${id}`
  const initialRef = useRef({ identity, value: defaultValue })
  if (initialRef.current.identity !== identity) {
    initialRef.current = { identity, value: defaultValue }
  }
  const hasPersistentSource = usesPersistentViewState && application != null && id.length > 0
  const instanceState = hasPersistentSource && scope === ViewStateScope.Route ? documentState : null
  const instanceStore = instanceState?.store
  const instanceId = instanceState?.instanceId

  const subscribePersistent = useCallback(
    (listener: () => void) =>
      instanceStore != null && instanceId != null
        ? instanceStore.subscribe(instanceId, persistentKey, listener)
        : hasPersistentSource
          ? application.viewState.subscribe(persistentKey, listener)
          : subscribeNoop(),
    [application, hasPersistentSource, instanceStore, instanceId, persistentKey]
  )
  const getPersistentValue = useCallback(
    () =>
      instanceStore != null && instanceId != null
        ? instanceStore.get(instanceId, persistentKey, initialRef.current.value)
        : hasPersistentSource
          ? application.viewState.get(persistentKey, initialRef.current.value)
          : initialRef.current.value,
    [application, hasPersistentSource, instanceStore, instanceId, persistentKey]
  )
  const getPersistentReady = useCallback(
    () =>
      instanceStore != null ? instanceStore.isReady : hasPersistentSource ? application.viewState.isRestored : true,
    [application, hasPersistentSource, instanceStore]
  )
  const persistentValue = useSyncExternalStore(subscribePersistent, getPersistentValue, () => initialRef.current.value)
  const persistentReady = useSyncExternalStore(subscribePersistent, getPersistentReady, () => false)
  useLayoutEffect(() => {
    if (persistentReady && instanceStore != null && instanceId != null) {
      instanceStore.initialize(instanceId, persistentKey, initialRef.current.value)
    }
  }, [persistentReady, instanceStore, instanceId, persistentKey])

  if (viewState !== ViewStateMode.None && id.length === 0) {
    throw new Error("viewStateId is required when viewState is enabled.")
  }

  if (usesPersistentViewState && application == null) {
    throw new Error('viewState="view" requires ApplicationProvider.')
  }

  if (viewState === ViewStateMode.URL && urlState == null) {
    throw new Error('viewState="url" requires ViewStateUrlProvider.')
  }

  if (viewState === ViewStateMode.URL) {
    assertUrlViewStateValue(initialRef.current.value)
  } else if (usesPersistentViewState) {
    assertViewStateValue(initialRef.current.value)
  }

  const urlValue =
    viewState === ViewStateMode.URL
      ? (decodeUrlViewState(urlState!.values[id], initialRef.current.value as UrlViewStateValue) as T)
      : initialRef.current.value
  const setUrlValue = useCallback(
    (updater: ViewStateUpdater<T>) => {
      if (urlState == null) return
      const nextValue = resolveViewStateUpdater(updater, urlValue)
      assertUrlViewStateValue(nextValue)
      urlState.onValueChange(
        id,
        Object.is(nextValue, initialRef.current.value) ? undefined : encodeUrlViewState(nextValue)
      )
    },
    [id, urlState, urlValue]
  )
  const resetUrlValue = useCallback(() => {
    urlState?.onValueChange(id, undefined)
  }, [id, urlState])
  const setPersistentValue = useCallback(
    (updater: ViewStateUpdater<T>) => {
      if (!hasPersistentSource) return
      if (instanceStore != null && instanceId != null) {
        const current = instanceStore.get(instanceId, persistentKey, initialRef.current.value)
        const next = resolveViewStateUpdater(updater, current)
        if (!Object.is(current, next)) instanceStore.set(instanceId, persistentKey, next)
        return
      }
      const current = application.viewState.get(persistentKey, initialRef.current.value)
      application.viewState.set(persistentKey, resolveViewStateUpdater(updater, current))
    },
    [application, hasPersistentSource, instanceStore, instanceId, persistentKey]
  )
  const resetPersistentValue = useCallback(() => {
    if (instanceStore != null && instanceId != null) {
      instanceStore.set(instanceId, persistentKey, initialRef.current.value)
    } else if (hasPersistentSource) application.viewState.remove(persistentKey)
  }, [application, hasPersistentSource, instanceStore, instanceId, persistentKey])

  return useMemo(() => {
    if (viewState === ViewStateMode.URL) {
      return {
        isReady: true,
        value: urlValue,
        setValue: setUrlValue,
        reset: resetUrlValue,
      }
    }

    if (usesPersistentViewState) {
      return {
        isReady: persistentReady,
        value: persistentValue,
        setValue: setPersistentValue,
        reset: resetPersistentValue,
      }
    }

    return {
      isReady: true,
      value: initialRef.current.value,
      setValue: noopSetValue,
      reset: noop,
    }
  }, [
    persistentReady,
    persistentValue,
    resetPersistentValue,
    resetUrlValue,
    setPersistentValue,
    setUrlValue,
    urlValue,
    usesPersistentViewState,
    viewState,
  ])
}

export function useViewStateValue<T extends ViewStateValue = never>(
  viewState: Exclude<ViewStateMode, "none">,
  viewStateId: string,
  defaultValue: NoInfer<T>
): T
export function useViewStateValue<T extends ViewStateValue>(
  viewState: Exclude<ViewStateMode, "none">,
  viewStateId: string,
  defaultValue: T
): ValidViewState<WidenInferredViewState<T>>
export function useViewStateValue(
  viewState: Exclude<ViewStateMode, "none">,
  viewStateId: string,
  defaultValue: ViewStateValue
) {
  return useViewStateField({ defaultValue, viewState, viewStateId }).value
}

export function useUrlViewStateValue<T extends UrlViewStateValue = never>(
  viewStateId: string,
  defaultValue: NoInfer<T>
): T
export function useUrlViewStateValue<T extends UrlViewStateValue>(
  viewStateId: string,
  defaultValue: T
): ValidViewState<WidenInferredViewState<T>>
export function useUrlViewStateValue(viewStateId: string, defaultValue: UrlViewStateValue) {
  return useViewStateField({
    defaultValue,
    viewState: ViewStateMode.URL,
    viewStateId,
  }).value
}

export function usePersistentViewStateValue<T extends ViewStateValue = never>(
  viewStateId: string,
  defaultValue: NoInfer<T>
): T
export function usePersistentViewStateValue<T extends ViewStateValue>(
  viewStateId: string,
  defaultValue: T
): ValidViewState<WidenInferredViewState<T>>
export function usePersistentViewStateValue(viewStateId: string, defaultValue: ViewStateValue) {
  return useViewStateField({
    defaultValue,
    viewState: ViewStateMode.View,
    viewStateId,
  }).value
}

function resolveViewStateUpdater<T extends ViewStateValue>(updater: ViewStateUpdater<T>, current: T) {
  return updater instanceof Function ? updater(current) : updater
}

function joinViewStateScope(parent: string, child: string) {
  const normalizedParent = parent.trim().replace(/^\/+|\/+$/g, "")
  const normalizedChild = child.trim().replace(/^\/+|\/+$/g, "")
  if (normalizedChild.length === 0) return normalizedParent
  if (normalizedParent.length === 0) return normalizedChild
  return `${normalizedParent}/${normalizedChild}`
}

function subscribeNoop() {
  return () => undefined
}

function noop() {}

function noopSetValue() {}
